/** Vessel from GET /api/vessels (Axiom Overwatch). */
export interface Vessel {
  id: string;
  imo: string | null;
  mmsi: string | null;
  name: string | null;
  callsign: string | null;
  lat: number;
  lon: number;
  speed: number | null;
  course: number | null;
  heading: number | null;
  vesselType: string | null;
  flag: string | null;
  destination: string | null;
  navStatus: string | null;
  draft: number | null;
  length: number | null;
  beam: number | null;
  dwt: number | null;
  buildYear: number | null;
  photoUrl: string | null;
  seen: number;
  source: 'axiomoverwatch';
}

export interface VesselsResponse {
  time: number;
  vessels: Vessel[];
  fromCache: boolean;
  providersHit?: string[];
  partial?: boolean;
}

/** Satellite OMM catalog entry from GET /api/satellites. */
export interface Satellite {
  noradId: string;
  name: string;
  objectId: string;
  epoch: string;
  meanMotion: number;
  eccentricity: number;
  inclination: number;
  raOfAscNode: number;
  argOfPericenter: number;
  meanAnomaly: number;
  bstar: number;
  classification: string;
  objectType: string | null;
  omm: Record<string, unknown>;
}

export interface SatellitesResponse {
  fetchedAt: number;
  catalog: Satellite[];
  fromCache: boolean;
  providersHit?: string[];
  partial?: boolean;
  error?: string;
}

export interface VesselFilter {
  query: string;
  vesselTypes: string[];
  iconKinds: string[];
  flags: string[];
  classes: string[];
  speedMin: number | null;
  speedMax: number | null;
  navStatuses: string[];
}

export interface SatelliteFilter {
  query: string;
  kinds: string[];
  classes: string[];
  regimes: string[];
  altMin: number | null;
  altMax: number | null;
  speedMin: number | null;
  speedMax: number | null;
  inclMin: number | null;
  inclMax: number | null;
  periodMin: number | null;
  periodMax: number | null;
  owners: string[];
}

export const DEFAULT_VESSEL_FILTER: VesselFilter = {
  query: '',
  vesselTypes: [],
  iconKinds: [],
  flags: [],
  classes: [],
  speedMin: null,
  speedMax: null,
  navStatuses: [],
};

export const DEFAULT_SATELLITE_FILTER: SatelliteFilter = {
  query: '',
  kinds: [],
  classes: [],
  regimes: [],
  altMin: null,
  altMax: null,
  speedMin: null,
  speedMax: null,
  inclMin: null,
  inclMax: null,
  periodMin: null,
  periodMax: null,
  owners: [],
};

/** Raw propagated satellite position (before enrichment). */
export interface SatellitePositionRaw {
  noradId: string;
  lat: number;
  lon: number;
  altKm: number;
  ecef: [number, number, number];
  speedKmS: number;
}

export type SatelliteKind =
  | 'starlink'
  | 'station'
  | 'navigation'
  | 'agency'
  | 'military'
  | 'geocomms'
  | 'constellation'
  | 'debris';

export type SatelliteRegime = 'LEO' | 'MEO' | 'GEO' | 'deep-space';

export type SatelliteClass = 'commercial' | 'government' | 'military' | 'unknown';

/** Enriched satellite for globe UI and filters. */
export interface RichSatellite extends Satellite {
  kind: SatelliteKind;
  satelliteClass: SatelliteClass;
  regime: SatelliteRegime;
  altitudeKm: number;
  speedKmS: number;
  inclinationDeg: number;
  periodMin: number;
  ageDays: number;
  ownerCountry: string | null;
  position: SatellitePositionRaw;
}
